/**
 * Drug Search Hook
 * Manages drug search state, filters, and pagination
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import useAPICall from '../../../../hooks/useAPICall';
import useDebounce from '../../../../hooks/useDebounce';
import usePagination from '../../../../hooks/usePagination';
import { API_CONFIG } from '../../../../config/api.config';
import { buildDrugQuery, normalizeSolrArray } from '../../../../utils/solrQuery';
import apiClient from '../../../../services/apiClient';

export const useDrugSearch = () => {
  const { loading, error, data, execute } = useAPICall();
  const [filters, setFilters] = useState({
    name: '',
    brandName: '',
    brandType: '',
    productSum: '',
  });
  const [facets, setFacets] = useState({
    brandNames: [],
    brandTypes: [],
    productSums: [],
  });
  const [autoSearch, setAutoSearch] = useState(false);

  const { page, pageSize, setPage, setPageSize, reset: resetPagination } = usePagination(1, 10);
  const debouncedFilters = useDebounce(filters, 500);
  const hasSearchedRef = useRef(false);
  const paginationRef = useRef({ page, pageSize });

  useEffect(() => {
    const loadFacets = async () => {
      try {
        const cached = {
          brandNames: sessionStorage.getItem('brand_name'),
          brandTypes: sessionStorage.getItem('brand_type'),
          productSums: sessionStorage.getItem('npa_product_sum'),
        };

        if (cached.brandNames && cached.brandTypes && cached.productSums) {
          setFacets({
            brandNames: cached.brandNames.split(','),
            brandTypes: cached.brandTypes.split(','),
            productSums: cached.productSums.split(','),
          });
          return;
        }

        const response = await apiClient.post(API_CONFIG.drug.facet.endpoint, { q: 'q=*:* &rows=1000' });
        const facetsData = response?.data?.facet_counts?.facet_fields || {};
        const brandNames = (facetsData.brand_name || []).filter((_, idx) => idx % 2 === 0);
        const brandTypes = (facetsData.brand_type || []).filter((_, idx) => idx % 2 === 0);
        const productSums = (facetsData.npa_product_sum || []).filter((_, idx) => idx % 2 === 0);

        sessionStorage.setItem('brand_name', brandNames.join(','));
        sessionStorage.setItem('brand_type', brandTypes.join(','));
        sessionStorage.setItem('npa_product_sum', productSums.join(','));

        setFacets({
          brandNames,
          brandTypes,
          productSums,
        });
      } catch (err) {
        setFacets({ brandNames: [], brandTypes: [], productSums: [] });
      }
    };

    loadFacets();
  }, []);

  const search = useCallback(async () => {
    try {
      const q = buildDrugQuery(filters, page, pageSize);
      await execute(
        API_CONFIG.drug.search,
        { q },
        {
          errorMessage: 'Unable to search drugs. Please try again.',
          retries: 1,
        }
      );
    } catch (err) {
      console.error('Drug search error:', err);
    } finally {
      hasSearchedRef.current = true;
    }
  }, [execute, filters, page, pageSize]);

  useEffect(() => {
    if (autoSearch && Object.values(debouncedFilters).some(Boolean)) {
      resetPagination();
      search();
    }
  }, [debouncedFilters, autoSearch, search, resetPagination]);

  // Initial search (legacy behavior) - run once
  useEffect(() => {
    if (!hasSearchedRef.current) {
      search();
    }
  }, [search]);

  // Re-run search when pagination changes after initial search
  useEffect(() => {
    const previous = paginationRef.current;
    const changed = previous.page !== page || previous.pageSize !== pageSize;
    paginationRef.current = { page, pageSize };
    if (hasSearchedRef.current && changed) {
      search();
    }
  }, [page, pageSize, search]);

  const updateFilters = useCallback((newFilters) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
  }, []);

  const clearFilters = useCallback(() => {
    setFilters({
      name: '',
      brandName: '',
      brandType: '',
      productSum: '',
    });
    resetPagination();
  }, [resetPagination]);

  const solr = data?.response ? data : (data?.data ? data.data : {});

  const normalizeSingleValue = (value) => {
    if (Array.isArray(value)) return value[0] ?? '';
    return value ?? '';
  };

  return {
    loading: loading || !hasSearchedRef.current,
    error,
    results: (solr?.response?.docs || []).map((doc) => ({
      id: normalizeSingleValue(doc.drug_id) || normalizeSingleValue(doc.brand_id) || doc.id,
      drugId: normalizeSingleValue(doc.drug_id),
      brandId: normalizeSingleValue(doc.brand_id),
      ndc: normalizeSolrArray(doc.ndc),
      drugName: normalizeSolrArray(doc.drug_name),
      brandName: normalizeSolrArray(doc.brand_name),
      formTlc3: normalizeSolrArray(doc.form_tlc3 || doc.formtlc),
      clientDrugs: normalizeSolrArray(doc.client_drug_flag),
      clientDrugFlag:
        doc.client_drug_flag === true
          || String(doc.client_drug_flag).toLowerCase() === 'true',
      manufacturerName: normalizeSolrArray(doc.manufacturer_name),
      manufacturer: normalizeSolrArray(doc.manufacturer_name),
      productSum: normalizeSolrArray(doc.npa_product_sum),
      brandType: normalizeSolrArray(doc.brand_type),
      clientBrandType: normalizeSolrArray(doc.client_brand_type),
      pgnKey: normalizeSolrArray(doc.xpt_pgn_key),
      pgnDescription: normalizeSolrArray(doc.xpt_pgn_desc),
    })),
    totalCount: solr?.response?.numFound || 0,
    filters,
    facets,
    page,
    pageSize,
    autoSearch,
    search,
    updateFilters,
    clearFilters,
    setPage,
    setPageSize,
    setAutoSearch,
  };
};

export default useDrugSearch;
